import React, { useEffect, useRef } from 'react';
import styles from './PortfolioModal.module.css';
import { PortfolioItem } from '../../../types';
import { FiX, FiCheckCircle } from 'react-icons/fi';
import clsx from 'clsx';

interface PortfolioModalProps {
  item: PortfolioItem;
  onClose: () => void;
}

export const PortfolioModal: React.FC<PortfolioModalProps> = ({ item, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Focus trap: move focus into modal on open, keep it there while open
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableSelectors = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const getFocusables = () => Array.from(modal.querySelectorAll<HTMLElement>(focusableSelectors));

    getFocusables()[0]?.focus();

    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusables();
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    modal.addEventListener('keydown', trap);
    return () => modal.removeEventListener('keydown', trap);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
          <FiX size={24} />
        </button>

        <div className={styles.modalContent}>
          {/* Image Section */}
          <div className={styles.imageSection}>
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={`${item.title} app screenshot`}
                className={styles.image}
              />
            ) : (
              <div className={styles.imagePlaceholder}>
                <span className={styles.imagePlaceholderText}>{item.title}</span>
              </div>
            )}
            <span className={clsx(
              styles.statusBadge,
              item.status === 'live' ? styles.statusLive : styles.statusDev
            )}>
              {item.status === 'live' ? 'Live on App Store' : 'In Development'}
            </span>
          </div>

          {/* Content Section */}
          <div className={styles.contentSection}>
            <div className={styles.header}>
              <h2 id="modal-title" className={styles.title}>{item.title}</h2>
            </div>

            <p className={styles.longDescription}>
              {item.longDescription || item.description}
            </p>

            {/* Technologies */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Technologies Used</h3>
              <div className={styles.technologies}>
                {item.technologies.map((tech) => (
                  <span key={tech} className={styles.tech}>{tech}</span>
                ))}
              </div>
            </div>

            {/* Results/Impact */}
            {item.results && item.results.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Key Results & Impact</h3>
                <ul className={styles.results}>
                  {item.results.map((result, index) => (
                    <li key={index} className={styles.result}>
                      <FiCheckCircle className={styles.checkIcon} />
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Links */}
            {(item.demoUrl || item.githubUrl) && (
              <div className={styles.links}>
                {item.demoUrl && (
                  <a href={item.demoUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    View Demo
                  </a>
                )}
                {item.githubUrl && (
                  <a href={item.githubUrl} target="_blank" rel="noopener noreferrer" className={styles.linkSecondary}>
                    View Code
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
